import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { RootState } from '../../app/store'
import { PaymentHistory as PaymentHistoryType } from '../../hooks/usePartialPayment'
import { formatNumberEnglish } from '../../utils'
import CurrencyDisplay from '../shared/CurrencyDisplay'
import cashIcon from '../../assets/images/cash.svg'
import onlineIcon from '../../assets/images/online.svg'

interface PaymentHistoryProps {
  payments: PaymentHistoryType[]
}

const PaymentHistory: React.FC<PaymentHistoryProps> = ({ payments }) => {
  const { t, i18n } = useTranslation()
  const { settings } = useSelector((state: RootState) => state.settings)
  
  // Ensure i18n language matches settings
  useEffect(() => {
    if (i18n.language !== settings.language) {
      i18n.changeLanguage(settings.language)
    }
  }, [settings.language, i18n])

  // Memoize translations to ensure they update when language changes
  const translations = useMemo(() => ({
    paid: t('payment.paid'),
    paymentMethod: t('payment.payment_method'),
    paymentStatus: t('payment.payment_status'),
    success: t('payment.success'),
    failed: t('payment.failed'),
    transactionId: t('payment.transaction_id'),
    network: t('payment.network'),
    cash: t('payment.cash'),
  }), [t, i18n.language])

  // Get payment method display name
  const getPaymentMethodName = (method: 'cash' | 'network'): string => {
    return method === 'network' ? translations.network : translations.cash
  }

  // Get payment method icon
  const getPaymentMethodIcon = (method: 'cash' | 'network') => {
    if (method === 'network') {
      return (
        <div className="size-4 relative overflow-hidden">
          <img src={onlineIcon} alt={translations.network} className="w-4 h-4" />
        </div>
      )
    } else {
      return (
        <div className="size-4 relative overflow-hidden">
          <img src={cashIcon} alt={translations.cash} className="w-4 h-4" />
        </div>
      )
    }
  }

  if (payments.length === 0) {
    return null
  }

  return (
    <div className="w-full flex flex-col gap-4">
      {payments.map((payment) => (
        <div key={payment.id} className=" p-4 bg-white rounded-2xl flex flex-col justify-start items-end gap-3">
          <div className="w-full  flex flex-col justify-start items-start gap-2">
            {/* Paid Amount */}
            <div className=" inline-flex w-full justify-between items-center">
            <div className="  text-text-tertiary text-sm font-normal font-['IBM_Plex_Sans_Arabic'] leading-5">
                {translations.paid}
              </div>
              <div className="flex justify-start items-center gap-1">
                <div className="flex items-center gap-1">
                  <span className=" justify-start text-text-primary text-sm font-medium font-['IBM_Plex_Sans_Arabic'] leading-5" >
                    {formatNumberEnglish(payment.amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  <CurrencyDisplay className="w-4 h-4" />
                </div>
              </div>
              
            </div>

            {/* Payment Method */}
            <div className="inline-flex w-full justify-between items-center">
            <div className=" justify-start text-text-tertiary text-sm font-normal font-['IBM_Plex_Sans_Arabic'] leading-5">
                {translations.paymentMethod}
             
              </div>
             
             <div className="flex justify-start items-center gap-1">
              {getPaymentMethodIcon(payment.paymentMethod)}
              
              <div className="justify-start text-text-primary text-sm font-normal font-['IBM_Plex_Sans_Arabic'] leading-5">
                {getPaymentMethodName(payment.paymentMethod)}
              </div>
              </div>
            </div>

            {/* Payment Status */}
            <div className="inline-flex w-full justify-between items-center">
            <div className="flex-1  justify-start text-text-tertiary text-sm font-normal font-['IBM_Plex_Sans_Arabic'] leading-5">
                {translations.paymentStatus}
              </div>

              <div className="flex justify-start items-center gap-1">
 
              <div className={`justify-start text-sm font-normal font-['IBM_Plex_Sans_Arabic'] leading-5 ${
                payment.status === 'success' ? 'text-green-600' : 'text-red-600'
              }`}>
                {payment.status === 'success' 
                  ? translations.success 
                  : translations.failed}
              </div>
              <div className={`size-4 ${payment.status === 'success' ? 'bg-green-600' : 'bg-red-600'} rounded-full`} />
           

              </div>
             
            
            </div>

            {/* Transaction ID */}
            {payment.transactionId && (
              <div className="inline-flex w-full justify-between items-center">
                 <div className=" justify-start text-text-tertiary text-sm font-normal font-['IBM_Plex_Sans_Arabic'] leading-5">
                  {translations.transactionId}
                </div>
                <div className=" justify-start text-text-primary text-sm font-normal font-['Inter'] leading-5">
                  {payment.transactionId}
                </div>
             
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

export default PaymentHistory

